import { useApiClient } from "@/modules/api/apiClient.store.ts";
import type { ApiClient } from "@/modules/api/apiClient.ts";
import type { Store } from "@/utils/jotai/types.ts";
import type { QueryClient, QueryKey } from "@tanstack/query-core";
import {
    useMutation,
    useQuery,
    useQueryClient,
    useSuspenseQuery,
    type UseMutationOptions,
    type UseSuspenseQueryOptions,
} from "@tanstack/react-query";
import { useStore } from "jotai/react";

export function useStandardMutation<TData, TError, TVariables, TContext>(
    fn: (api: ApiClient, query: QueryClient, store: Store) => UseMutationOptions<TData, TError, TVariables, TContext>,
) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const store = useStore();
    return useMutation(fn(apiClient, queryClient, store));
}

export function useStandardSuspenseQuery<
    TArgs extends unknown[],
    TData,
    TError,
    TVariables,
    TQueryKey extends QueryKey,
>(
    fn: (api: ApiClient, ...args: TArgs) => UseSuspenseQueryOptions<TData, TError, TVariables, TQueryKey>,
    ...args: TArgs
) {
    const apiClient = useApiClient();
    return useSuspenseQuery(fn(apiClient, ...args));
}

export function useStandardQuery<TArgs extends unknown[], TData, TError, TVariables, TQueryKey extends QueryKey>(
    fn: (api: ApiClient, ...args: TArgs) => UseSuspenseQueryOptions<TData, TError, TVariables, TQueryKey>,
    ...args: TArgs
) {
    const apiClient = useApiClient();
    return useQuery(fn(apiClient, ...args));
}
